// Main.tsx
import React, { useEffect } from 'react'
import toast, { Toaster } from 'react-hot-toast'
import { Breadcrumb } from '../../../../components/ui/breadcrumb'
import { Label } from '../../../../components/ui/label'
import { Combobox } from '../../../../components/ui/combobox'
import { useNavigate } from 'react-router-dom'
import axios from '../../../../lib/axios'
import useAuth from '../../../../hooks/useAuth'
import { useSelector } from 'react-redux'
import { RootState } from '../../../../redux'
import { Input } from '../../../../components/ui/input'
import { Button } from '../../../../components/ui/button'
import { useQuery } from '@tanstack/react-query'
import { Loader2 } from 'lucide-react'
import { isInterface } from '../../../../lib/utils'
import E2ETable from './tables/E2ETable'
import { getCustomerList } from '../../../../lib/fetch/customer'
import { useTranslation } from 'react-i18next'
import WindowsDatePicker from '../../../../components/ui/windows-datepicker'
import { formatDateToYYYYMMDD } from '../../../../lib/date'
import useE2EFilterStore from '../../../../zustand/useE2EFilterStore'

const Main = () => {
  const navigate = useNavigate()
  const { auth } = useAuth()
  const { t } = useTranslation()
  const partners = useSelector((state: RootState) => state.partners)
  const {
    selected_partner, setselected_partner,
    selected_customer, setselected_customer,
    selected_clientcode, setselected_clientcode,
    selected_atmid, setselected_atmid,
    selected_startdate, setselected_startdate,
    selected_enddate, setselected_enddate,
    customers_list, setcustomers_list,
  } = useE2EFilterStore()

  const { data: e2e_services, isFetching, refetch } = useQuery<any[] | null>({
    queryKey: ['e2e_services', selected_partner, selected_customer, selected_clientcode, selected_atmid, selected_startdate, selected_enddate],

    queryFn: async () => {
      try {
        const params: any = { token: auth.data?.token }
        if (selected_customer && selected_customer !== "clear" && selected_customer !== process.env.REACT_APP_RDM_NUM) {
          params.relnr = selected_customer
        }
        if (selected_partner && selected_partner !== process.env.REACT_APP_RDM_NUM && selected_partner !== null) {
          params.partnerid = selected_partner
        }
        if (selected_clientcode && selected_clientcode.trim() !== '') {
          params.klantcode = selected_clientcode
        }
        if (selected_atmid && selected_atmid.trim() !== '') {
          params.atmid = selected_atmid
        }
        params.startdate = formatDateToYYYYMMDD(selected_startdate)
        params.enddate = formatDateToYYYYMMDD(selected_enddate)

        const { data } = await axios.get(`e2eservice`, { params })

        if (data.errorcode !== 0) throw new Error()

        const nestedServices = data.Object || data.object

        if (!nestedServices || nestedServices.length === 0) {
          toast.error(t("sip.o_e2e.toast_no_e2e"))
          return null
        }

        // Flatten the nested structure: extract laE2e from each item
        const flattenedServices = nestedServices.flatMap((item: any) => {

          return (item.laE2e || []).map((service: any) => ({
            ...service,
            lsPartner: item.lsPartner // Add partner name from parent level
          }))
        })

        if (flattenedServices.length === 0) {
          toast.error(t("sip.o_e2e.toast_no_e2e"))
          return null
        }

        return flattenedServices
      } catch (error) {
        console.error('❌ Main.tsx - E2eService error:', error)
        toast.error(t("sip.o_e2e.toast_wrong_e2e"))
        return null
      }
    },


    enabled: false
  })

  useEffect(() => {
    if (selected_partner !== null) return
    const filtered = Array.isArray(partners) ? partners : []
    if (filtered.length === 1) {
      handlePickPartner(filtered[0].relnr.toString())
    }
  }, [partners])

  const handlePickPartner = async (relnr: string) => {
    try {
      if (selected_partner !== relnr) setselected_customer('clear')
      setselected_partner(relnr)
      setcustomers_list(null)
      const list = await getCustomerList(auth, relnr)
      if (list) {
        setcustomers_list(list)
        if (list.length === 1) setselected_customer(list[0].relnr.toString())
      }
    } catch (error) {
      console.error('❌ Main.tsx - handlePickPartner error:', error)
      toast.error(t("sip.o_e2e.toast_wrong"))
    }
  }

  let partnersArr = Array.isArray(partners)
    ? partners.map(item => ({ value: item.relnr, label: item.name }))
    : []
  if (partnersArr.length > 0) {
    partnersArr.unshift({
      value: parseFloat(process.env.REACT_APP_RDM_NUM!),
      label: t("components.o_partners_ddl.no_partner")
    })
  }

  let customersArr = Array.isArray(customers_list)
    ? customers_list.map(item => ({
      value: item.relnr,
      label: `${item.name} - ${item.clientcode || 'geen code'}`
    }))
    : []
  if (customersArr.length > 0) {
    customersArr.unshift({
      value: parseFloat(process.env.REACT_APP_RDM_NUM!),
      label: t("components.o_partners_ddl.no_customer")
    })
  }

  const selectedPartnerLabel =
    selected_partner && selected_partner !== process.env.REACT_APP_RDM_NUM
      ? partnersArr.find(p => String(p.value) === selected_partner)?.label ?? null
      : null

  const selectedCustomerLabel =
    selected_customer && selected_customer !== process.env.REACT_APP_RDM_NUM && selected_customer !== 'clear'
      ? customersArr.find(c => String(c.value) === selected_customer)?.label ?? null
      : null

  return (
    <>
      <Toaster />
      <div className="overflow-auto relative w-full p-4 md:p-8">
        <Breadcrumb items={[
          { title: <p onClick={() => navigate('..')} className="cursor-pointer">{t("sip.o_e2e.link_sip")}</p> },
          { title: t("sip.o_e2e.link_e2e") }
        ]} />
        <h1 className="text-2xl font-semibold">{t("sip.o_e2e.title")}</h1>

        <div className="flex flex-col gap-4 p-4 shadow-sm border rounded-sm mt-4 md:mt-8">
          <div className="grid grid-cols-[repeat(auto-fit,minmax(300px,1fr))] gap-4">
            <div>
              <Label htmlFor="partner">{t("sip.o_e2e.partner")}</Label>
              <Combobox
                disabled={partners?.length === 1}
                id="partner"
                selectedValue={selected_partner ?? undefined}
                onValueChange={value => handlePickPartner(String(value.value))}
                options={partnersArr}
                placeholder={t("sip.o_e2e.partner_placeholder")}
                input_placeholder={t("sip.o_e2e.search_placeholder")}
              />
            </div>
            <div>
              <Label htmlFor="end_customer">{t("sip.o_e2e.customer")}</Label>
              <Combobox
                disabled={!Array.isArray(customers_list)}
                selectedValue={selected_customer ?? undefined}
                id="end_customer"
                onValueChange={value => setselected_customer(String(value.value))}
                options={customersArr}
                placeholder={t("sip.o_e2e.customer_placeholder")}
                input_placeholder={t("sip.o_e2e.search_placeholder")}
              />
            </div>
            <div>
              <Label htmlFor="clientcode">{t("sip.o_e2e.customer_code")}</Label>
              <Input
                value={selected_clientcode}
                onChange={e => setselected_clientcode(e.currentTarget.value)}
                id="clientcode"
              />
            </div>
            <div>
              <Label htmlFor="atmid">{t("sip.o_e2e.atm_id")}</Label>
              <Input
                value={selected_atmid}
                onChange={e => setselected_atmid(e.currentTarget.value)}
                id="atmid"
              />
            </div>
            <div>
              <Label htmlFor="startdate">{t("sip.o_atm.date_start")}</Label>
              <WindowsDatePicker
                value={selected_startdate}
                onDateChange={value => { if (value) setselected_startdate(value) }}
                id="startdate"
                placeholder={t("sip.o_atm.date_placeholder")}
              />
            </div>
            <div>
              <Label htmlFor="enddate">{t("sip.o_atm.date_end")}</Label>
              <WindowsDatePicker
                value={selected_enddate}
                onDateChange={value => { if (value) setselected_enddate(value) }}
                id="enddate"
                placeholder={t("sip.o_atm.date_placeholder")}
              />
            </div>
          </div>
          <Button
            onClick={() => refetch()}
            disabled={isFetching}
            className="w-fit"
          >
            {isFetching ? <Loader2 className="animate-spin" /> : t("sip.o_e2e.search")}
          </Button>
        </div>

        {isInterface(e2e_services, [] as any[]) && e2e_services && (
          <E2ETable
            services={e2e_services}
            selectedPartnerId={selected_partner}
            selectedPartnerName={selectedPartnerLabel}
            selectedCustomerId={selected_customer}
            selectedCustomerName={selectedCustomerLabel}
          />
        )}
      </div>
    </>
  )
}

export default Main